"use client";

import { useState } from "react";
import { AppIcon } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { IconPadlock } from "@/components/ui/icons";
import { login } from "@/features/auth/api/authClient";

/**
 * Prefilled credentials for local dev testing. Only rendered when not running
 * in production.
 */
const DEV_LOGINS = [
  {
    label: "Joe",
    schoolId: "A1930499544",
    wondeId: "A126615324",
    dateOfBirth: "2008-12-03",
  },
  {
    label: "Lewis",
    schoolId: "A1930499544",
    wondeId: "A83457830",
    dateOfBirth: "2008-09-03",
  },
] as const;

const showDevLogins = process.env.NODE_ENV !== "production";

/**
 * Passwordless, 3-factor login (ClassCharts-style): schoolId + wondeId +
 * dateOfBirth. Self-signup is impossible — the account already exists from the
 * school's Wonde sync. On success the Server issues httpOnly session cookies
 * and we send the student to their profile.
 */
export function LoginScreen() {
  const [schoolId, setSchoolId] = useState("");
  const [wondeId, setWondeId] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    schoolId.trim().length > 0 &&
    wondeId.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth) &&
    !submitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const outcome = await login({ schoolId, wondeId, dateOfBirth });
    if (outcome.ok) {
      // Full reload so UserContext + route guard pick up the new session.
      window.location.assign("/profile");
      return;
    }
    setError(outcome.error ?? "Login failed. Please try again.");
    setSubmitting(false);
  };

  const prefill = (creds: (typeof DEV_LOGINS)[number]) => {
    setError(null);
    setSchoolId(creds.schoolId);
    setWondeId(creds.wondeId);
    setDateOfBirth(creds.dateOfBirth);
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <AppIcon
            size={64}
            className="mx-auto mb-4 rounded-2xl shadow-sm ring-1 ring-edge"
          />
          <h1 className="text-2xl font-semibold tracking-tight text-fg">oStudent</h1>
          <p className="mt-2 text-sm text-muted">
            Sign in with your school details. There&apos;s nothing to create —
            your school has already set you up.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-3 rounded-2xl border border-edge bg-elevated p-5"
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted">School ID</span>
            <input
              value={schoolId}
              onChange={(event) => setSchoolId(event.target.value)}
              placeholder="e.g. A1930499544"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              className="h-12 w-full rounded-xl border border-edge bg-subtle px-4 text-sm text-fg outline-none placeholder:text-faint focus:border-accent"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted">Wonde ID</span>
            <input
              value={wondeId}
              onChange={(event) => setWondeId(event.target.value)}
              placeholder="e.g. A1930499544-A1161584171"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              className="h-12 w-full rounded-xl border border-edge bg-subtle px-4 text-sm text-fg outline-none placeholder:text-faint focus:border-accent"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted">
              Date of birth
            </span>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(event) => setDateOfBirth(event.target.value)}
              className="h-12 w-full rounded-xl border border-edge bg-subtle px-4 text-sm text-fg outline-none focus:border-accent"
            />
          </label>

          {error ? (
            <p
              role="alert"
              className="rounded-xl bg-danger/10 px-3 py-2 text-xs text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" fullWidth size="lg" disabled={!canSubmit}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          {showDevLogins ? (
            <div className="mt-1 border-t border-edge pt-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-faint">
                Dev quick fill
              </p>
              <div className="flex flex-wrap gap-2">
                {DEV_LOGINS.map((creds) => (
                  <button
                    key={creds.label}
                    type="button"
                    onClick={() => prefill(creds)}
                    disabled={submitting}
                    className="flex-1 rounded-xl border border-edge bg-subtle px-3 py-2 text-xs font-medium text-fg outline-none transition-colors hover:border-accent disabled:opacity-50"
                  >
                    {creds.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </form>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-[11px] text-faint">
          <IconPadlock size={12} />
          Secured by your school&apos;s MIS. Accounts cannot be created here.
        </p>
      </div>
    </div>
  );
}
